// Per-user "My Frames" library. Persists custom frame definitions in
// localStorage keyed by the signed-in user, so each account has its own set.
// The API is intentionally storage-agnostic (all CRUD goes through read/write)
// so it can later be backed by a server endpoint without touching callers.

const keyFor = (userId) => `ivisora_custom_frames_${userId || 'guest'}`;
const genId = () => `cf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Identify the signed-in user (so each account gets its own My Frames). Falls
// back to 'guest' for anonymous sessions.
export function currentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem('ivisora_user') || 'null');
    return (u && (u.id || u.email)) || 'guest';
  } catch {
    return 'guest';
  }
}

function read(userId) {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function write(userId, list) {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(list));
  } catch {
    /* quota / unavailable — non-fatal */
  }
}

export function listCustomFrames(userId) {
  return read(userId);
}

// frame: { name, framePath, frameViewBox, frameSource, thumbnail }
export function saveCustomFrame(userId, frame) {
  if (!frame || !frame.framePath) return null;
  const item = {
    id: genId(),
    name: (frame.name || 'My Frame').trim() || 'My Frame',
    framePath: frame.framePath,
    frameViewBox: frame.frameViewBox || { w: 1, h: 1 },
    frameSource: frame.frameSource || 'shape',
    thumbnail: frame.thumbnail || null,
    createdAt: Date.now(),
  };
  write(userId, [item, ...read(userId)]);
  return item;
}

export function renameCustomFrame(userId, id, name) {
  const next = read(userId).map((f) => (f.id === id ? { ...f, name: (name || '').trim() || f.name } : f));
  write(userId, next);
  return next.find((f) => f.id === id) || null;
}

export function duplicateCustomFrame(userId, id) {
  const list = read(userId);
  const src = list.find((f) => f.id === id);
  if (!src) return null;
  const copy = { ...src, id: genId(), name: `${src.name} copy`, createdAt: Date.now() };
  write(userId, [copy, ...list]);
  return copy;
}

export function deleteCustomFrame(userId, id) {
  write(userId, read(userId).filter((f) => f.id !== id));
}

export function searchCustomFrames(userId, query) {
  const q = (query || '').toLowerCase().trim();
  const list = read(userId);
  return q ? list.filter((f) => f.name.toLowerCase().includes(q)) : list;
}
